import React, {useCallback, useEffect} from 'react'
import {
  isTranslationKey,
  loginErrorQueryParam,
  logoutMessageQueryParam,
  logoutTitleQueryParam,
  type TranslationKey
} from 'notability-services-common-universal'
import {
  NOTABILITY_AD_LANDING_URL_KEY,
  SINGULAR_TRACKING_LINK_PARAMS_KEY,
  TRACKING_LINK_PARAMS
} from 'notability-services-common-frontend'
import {useAnalytics} from 'notability-services-next-frontend'
import {ComponentWithLoginContext} from '../util/LoginContext'
import {useCustomRouter} from '../util/useCustomRouter'
import {useSingular} from '../hooks/useSingular'
import {GetServerSideProps} from 'next'
import {getStrings} from '../util/localizationUtils'
import dynamic from 'next/dynamic'
import Script from 'next/script'
import {redirectToLoginComplete} from '../util/redirect'
import {LoginLayout} from '../components/LoginLayout'
import {config} from '../config'
import {sspWithLoginContext} from '../util/loginSSR'

interface LoginProps {
  errorKey: TranslationKey | null
  titleKey: TranslationKey | null
  messageKey: TranslationKey | null
}

export default ComponentWithLoginContext(Login)

const LoginForm = dynamic(() => import('../components/LoginForm'), {
  ssr: false
})

function Login({errorKey, titleKey, messageKey}: LoginProps) {
  const router = useCustomRouter()
  const singular = useSingular()
  const {setUserProperties} = useAnalytics()

  const getAdTrackingProperties = useCallback((): Record<string, string> => {
    const adTrackingProperties: Record<string, string> = {}
    const textEncoder = new TextEncoder()

    try {
      const storedTrackingParams = localStorage.getItem(SINGULAR_TRACKING_LINK_PARAMS_KEY)
      const adLandingUrl = localStorage.getItem(NOTABILITY_AD_LANDING_URL_KEY)

      if (storedTrackingParams) {
        try {
          const parsed = JSON.parse(storedTrackingParams)
          if (typeof parsed === 'object' && parsed !== null) {
            // Only include valid TRACKING_LINK_PARAMS
            TRACKING_LINK_PARAMS.forEach((param) => {
              if (parsed[param] && typeof parsed[param] === 'string') {
                // Sanitize and limit to Mixpanel's 255 byte limit https://docs.mixpanel.com/docs/data-structure/property-reference/data-type#string
                const value = String(parsed[param]).trim()
                if (value.length > 0 && textEncoder.encode(value).length <= 255) {
                  adTrackingProperties[param] = value
                }
              }
            })
          }
        } catch (e) {
          console.warn('Failed to parse stored tracking params:', e)
        }
      }

      // Only add landing URL if we have tracking parameters (user came from ads)
      if (adLandingUrl && typeof adLandingUrl === 'string' && Object.keys(adTrackingProperties).length > 0) {
        const sanitizedUrl = String(adLandingUrl).trim()
        if (sanitizedUrl.length > 0 && textEncoder.encode(sanitizedUrl).length <= 255) {
          adTrackingProperties.adLandingUrl = sanitizedUrl
        }
      }

      try {
        localStorage.removeItem(SINGULAR_TRACKING_LINK_PARAMS_KEY)
        localStorage.removeItem(NOTABILITY_AD_LANDING_URL_KEY)
      } catch (e) {}
    } catch (e) {
      console.warn('Failed to access localStorage for ad tracking properties:', e)
    }

    return adTrackingProperties
  }, [])

  useEffect(() => {
    singular?.trackEvent('sng_web_landing')

    setUserProperties(
      {
        'Origin Platform': 'Web',
        ...getAdTrackingProperties()
      },
      true
    )
  }, [singular, getAdTrackingProperties, setUserProperties])

  return (
    <LoginLayout pageTitle={'pageTitleLogin'}>
      {config.SHOULD_LOAD_TRACKING_PIXEL && (
        <Script id="smadex-pixel" strategy="afterInteractive">
          {`
          var u="https://geo-tracker.smadex.com/hyperad/pixel-tracking?order=115138&action=landing"; var t=new Date().getTime(); var ut=u+"&rand="+t; var it=document.createElement("img"); it.src=ut; it.border=0;
        `}
        </Script>
      )}
      <LoginForm
        onLoggedIn={() => router.pushLoginComplete()}
        errorKey={errorKey ?? undefined}
        titleKey={titleKey ?? undefined}
        messageKey={messageKey ?? undefined}
      />
    </LoginLayout>
  )
}

export const getServerSideProps: GetServerSideProps<LoginProps> = sspWithLoginContext<LoginProps>(async (context) => {
  const errorKey = isTranslationKey(context.query[loginErrorQueryParam]) ? context.query[loginErrorQueryParam] : null
  const titleKey = isTranslationKey(context.query[logoutTitleQueryParam]) ? context.query[logoutTitleQueryParam] : null
  const messageKey = isTranslationKey(context.query[logoutMessageQueryParam])
    ? context.query[logoutMessageQueryParam]
    : null

  const redirect = await redirectToLoginComplete(context)
  if (redirect) {
    return {redirect}
  }

  return {
    props: {
      ...(await getStrings(context)),
      errorKey: errorKey || null,
      messageKey,
      titleKey
    }
  }
})
